/**
 * SecurityDashboard - Admin security monitoring dashboard
 * Shows login sessions, suspicious activity, and security alerts
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Shield, AlertTriangle, Users, MapPin, Monitor, Smartphone,
  Tablet, Globe, Clock, CheckCircle, XCircle, Eye, Ban,
  RefreshCw, Filter, Search, ChevronDown, Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';

// Helper to get device icon
const getDeviceIcon = (deviceType) => {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return <Smartphone className="h-4 w-4" />;
    case 'tablet':
      return <Tablet className="h-4 w-4" />;
    default:
      return <Monitor className="h-4 w-4" />;
  }
};

// Helper to format location
const formatLocation = (geoInfo) => {
  if (!geoInfo) return 'Unknown';
  if (geoInfo.location === 'Local' || geoInfo.location === 'Private Network') {
    return geoInfo.location;
  }
  return geoInfo.country || geoInfo.location || 'Unknown';
};

const SecurityDashboard = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuspiciousOnly, setShowSuspiciousOnly] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // Fetch security dashboard data
  const { data: dashboard, isLoading: dashboardLoading, refetch: refetchDashboard } = useQuery({
    queryKey: ['security-dashboard'],
    queryFn: async () => {
      const response = await api.get('/admin/security/dashboard');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch login sessions
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['login-sessions', userTypeFilter, showSuspiciousOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (userTypeFilter !== 'all') params.append('user_type', userTypeFilter);
      if (showSuspiciousOnly) params.append('suspicious_only', 'true');
      params.append('limit', '100');
      
      const response = await api.get(`/admin/security/login-sessions?${params}`);
      return response.data;
    },
  });

  // Fetch security alerts
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['security-alerts'],
    queryFn: async () => {
      const response = await api.get('/admin/security/alerts?limit=50');
      return response.data;
    },
  });

  // Terminate session mutation
  const terminateSession = useMutation({
    mutationFn: async ({ sessionId, reason }) => {
      const response = await api.post('/admin/security/login-sessions/terminate', {
        session_id: sessionId,
        reason: reason || 'Terminated by admin'
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Session terminated successfully');
      queryClient.invalidateQueries({ queryKey: ['login-sessions'] });
      setSelectedSession(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to terminate session');
    }
  });

  // Review alert mutation
  const reviewAlert = useMutation({
    mutationFn: async ({ alertId, action }) => {
      const response = await api.post(`/admin/security/alerts/${alertId}/review`, { action });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Alert reviewed');
      queryClient.invalidateQueries({ queryKey: ['security-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['security-dashboard'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to review alert');
    }
  });

  // Filter sessions by search query
  const filteredSessions = sessionsData?.sessions?.filter(session => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      session.email?.toLowerCase().includes(query) ||
      session.ip_address?.toLowerCase().includes(query) ||
      session.user_type?.toLowerCase().includes(query)
    );
  }) || [];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Security Monitor
          </h1>
          <p className="text-muted-foreground">
            Track login activity, detect suspicious access, and manage security alerts
          </p>
        </div>
        <Button onClick={() => refetchDashboard()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Logins Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.summary?.logins_24h || 0}</div>
            <p className="text-xs text-muted-foreground">
              {dashboard?.summary?.unique_users_today || 0} unique users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Failed Logins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">
              {dashboard?.summary?.failed_logins_24h || 0}
            </div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Suspicious Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {dashboard?.summary?.suspicious_24h || 0}
            </div>
            <p className="text-xs text-muted-foreground">Needs review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unreviewed Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {dashboard?.summary?.unreviewed_alerts || 0}
            </div>
            <p className="text-xs text-muted-foreground">Pending action</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Top IP
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-mono truncate">
              {dashboard?.top_ips?.[0]?.ip || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {dashboard?.top_ips?.[0]?.count || 0} logins
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sessions">Login Sessions</TabsTrigger>
          <TabsTrigger value="alerts">
            Security Alerts
            {(alertsData?.unreviewed_count || 0) > 0 && (
              <Badge variant="destructive" className="ml-2">
                {alertsData?.unreviewed_count}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Suspicious Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Recent Suspicious Activity
                </CardTitle>
                <CardDescription>
                  Logins from new IPs or with multiple failed attempts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {dashboard?.recent_suspicious?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <p>No suspicious activity detected</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dashboard?.recent_suspicious?.map((session) => (
                        <div
                          key={session.id}
                          className="p-3 border rounded-lg bg-yellow-50 dark:bg-yellow-900/10"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{session.email}</p>
                              <p className="text-sm text-muted-foreground">
                                {session.user_type} • {session.ip_address}
                              </p>
                            </div>
                            <Badge variant={session.success ? 'default' : 'destructive'}>
                              {session.success ? 'Success' : 'Failed'}
                            </Badge>
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {getDeviceIcon(session.device_info?.device_type)}
                              {session.device_info?.browser || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              {formatLocation(session.geo_info)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(session.timestamp), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Login Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-500" />
                  Logins by User Type
                </CardTitle>
                <CardDescription>
                  Distribution of logins in the last 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(dashboard?.logins_by_type || {}).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {type.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{
                              width: `${Math.min(100, (count / (dashboard?.summary?.logins_24h || 1)) * 100)}%`
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-12 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-3">Top IP Addresses</h4>
                  <div className="space-y-2">
                    {dashboard?.top_ips?.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {item.ip}
                        </code>
                        <span className="text-muted-foreground">{item.count} logins</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, IP, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
              <SelectTrigger className="w-full max-w-[150px]">
                <SelectValue placeholder="User type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="delegate">Delegate</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={showSuspiciousOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowSuspiciousOnly(!showSuspiciousOnly)}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Suspicious Only
            </Button>
          </div>

          {/* Sessions Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        Loading sessions...
                      </TableCell>
                    </TableRow>
                  ) : filteredSessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No login sessions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSessions.map((session) => (
                      <TableRow
                        key={session.id}
                        className={session.is_suspicious ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{session.email}</p>
                            {session.is_suspicious && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Suspicious
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {session.user_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {session.ip_address || 'N/A'}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(session.device_info?.device_type)}
                            <span className="text-sm">
                              {session.device_info?.browser || 'Unknown'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Globe className="h-3 w-3" />
                            {formatLocation(session.geo_info)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {session.timestamp ? (
                              <>
                                <p>{format(new Date(session.timestamp), 'MMM d, HH:mm')}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(session.timestamp), { addSuffix: true })}
                                </p>
                              </>
                            ) : (
                              'N/A'
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {session.success ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Success
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSelectedSession(session)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {session.success && (
                                <DropdownMenuItem
                                  onClick={() => terminateSession.mutate({
                                    sessionId: session.session_id,
                                    reason: 'Admin action'
                                  })}
                                  className="text-red-600"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Terminate Session
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Alerts</CardTitle>
              <CardDescription>
                Review and acknowledge security incidents
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="text-center py-8">Loading alerts...</div>
              ) : alertsData?.alerts?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No security alerts</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {alertsData?.alerts?.map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-4 border rounded-lg ${
                        alert.reviewed
                          ? 'bg-muted/50'
                          : alert.severity === 'warning'
                          ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10'
                          : 'bg-red-50 border-red-200 dark:bg-red-900/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <AlertTriangle
                            className={`h-5 w-5 mt-0.5 ${
                              alert.severity === 'warning' ? 'text-yellow-500' : 'text-red-500'
                            }`}
                          />
                          <div>
                            <p className="font-medium">{alert.message}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span>{alert.email}</span>
                              <span>•</span>
                              <span>{alert.ip_address}</span>
                              <span>•</span>
                              <span>
                                {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {alert.reviewed ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Reviewed
                            </Badge>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => reviewAlert.mutate({
                                  alertId: alert.id,
                                  action: 'acknowledged'
                                })}
                              >
                                Acknowledge
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => reviewAlert.mutate({
                                  alertId: alert.id,
                                  action: 'blocked'
                                })}
                              >
                                Block User
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Session Details Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
            <DialogDescription>
              Full information about this login session
            </DialogDescription>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Email</label>
                  <p className="font-medium">{selectedSession.email}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">User Type</label>
                  <p className="font-medium capitalize">{selectedSession.user_type}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">IP Address</label>
                  <p className="font-mono text-sm">{selectedSession.ip_address}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Location</label>
                  <p>{formatLocation(selectedSession.geo_info)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Device</label>
                  <p>{selectedSession.device_info?.device_type || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Browser</label>
                  <p>{selectedSession.device_info?.browser || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">OS</label>
                  <p>{selectedSession.device_info?.os || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Time</label>
                  <p>{selectedSession.timestamp && format(new Date(selectedSession.timestamp), 'PPpp')}</p>
                </div>
              </div>
              
              {selectedSession.failure_reason && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <label className="text-sm text-red-600">Failure Reason</label>
                  <p className="text-red-700">{selectedSession.failure_reason}</p>
                </div>
              )}

              {selectedSession.device_info?.raw && (
                <div>
                  <label className="text-sm text-muted-foreground">User Agent</label>
                  <p className="text-xs font-mono bg-muted p-2 rounded break-all">
                    {selectedSession.device_info.raw}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSession(null)}>
              Close
            </Button>
            {selectedSession?.success && (
              <Button
                variant="destructive"
                onClick={() => {
                  terminateSession.mutate({
                    sessionId: selectedSession.session_id,
                    reason: 'Admin action from details view'
                  });
                }}
              >
                Terminate Session
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityDashboard;
